import React, { Fragment, useState, useEffect } from "react";
import * as Entities from "entities";

import Box from "@material-ui/core/Box";
import { Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import Fab from "@material-ui/core/Fab";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Link from "@material-ui/core/Link";

import AddEditAdmForm from "./AddEditAdmForm";
import DeleteAdmForm from "./DeleteAdmForm";
import KeyValue from "../display/KeyValue";
import { fromUnixtime } from "../../utils/time";
import StatusChip from "../chips/StatusChip";
import { admAreaUrl } from "../../utils/url";

import { permissions, types, checkAcl } from "../../utils/acl";

const useStyles = makeStyles((theme) => ({
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
  },
  paper: {
    width: "100%",
    overflowX: "auto",
    marginBottom: theme.spacing(2),
    backgroundColor: "#212121",
    padding: "15px",
  },
}));

export default function AdmDetails({
  admDetails,
  level,
  setHasChild,
  setMapdetails,
}) {
  const classes = useStyles();
  const EDIT_ACCESS = checkAcl({ p: permissions.ADMINISTRATIVE_AREA_MANAGEMENT, t: types.VIEW_AND_EDIT });
  const DELETE_ACCESS = checkAcl({ p: permissions.ADMINISTRATIVE_AREA_MANAGEMENT, t: types.VIEW_EDIT_AND_DELETE });

  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [values, setValues] = useState();

  useEffect(() => {
    setValues({
      name: !!admDetails.name ? admDetails.name : "",
      nameAlt: !!admDetails.nameAlt ? admDetails.nameAlt : "",
      nameLocal: !!admDetails.nameLocal ? admDetails.nameLocal : "",
      hasChild: admDetails.hasChild,
      lat: !!admDetails.coordinates ? admDetails.coordinates.lat : 0,
      lng: !!admDetails.coordinates ? admDetails.coordinates.lng : 0,
      caption: !!admDetails.caption ? admDetails.caption : "",
      description: !!admDetails.description ? admDetails.description : "",
      link: !!admDetails.link ? admDetails.link : "",
      meta: {
        title: admDetails?.meta?.title ?? "",
        description: admDetails?.meta?.description ?? "",
      },
      googlePlaceId: !!admDetails.googlePlaceId ? admDetails.googlePlaceId : "",
      status: admDetails.status,
    });
  }, [admDetails]); //run only when admDetail changes

  function handleOpenEditModal() {
    setOpenEditModal(true);
  }

  function handleCloseEditModal() {
    setOpenEditModal(false);
  }

  function handleOpenDeleteModal() {
    setOpenDeleteModal(true);
  }

  function handleCloseDeleteModal() {
    setOpenDeleteModal(false);
  }

  function getType(param) {
    switch (param) {
      case 0:
        return "Country";
      case 1:
        return "Administrative Level 1";
      case 2:
        return "Administrative Level 2";
      default:
        return "Unknown";
    }
  }

  return (
    <Fragment>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="adm-detail-content"
          id="adm-detail-header"
        >
          <Typography className={classes.heading}>Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="span" display="block" width="100%">
            {!!values && !!values.name ? (
              <Fragment>
                <Paper className={classes.paper}>
                  <Box>
                    <Link
                      color="inherit"
                      href={admAreaUrl(admDetails.admId, values.name, true)}
                      underline="none"
                      target="_blank"
                      variant="subtitle1"
                      gutterBottom
                    >
                      {values.name}
                    </Link>
                  </Box>
                  <StatusChip status={values.status} />
                  <KeyValue keyName="ID" value={admDetails.admId} />
                  <KeyValue keyName="Short Name" value={values.nameAlt} />
                  <KeyValue keyName="Local Name" value={values.nameLocal} />
                  <KeyValue
                    keyName="Country Code"
                    value={admDetails.countryCode}
                  />
                  <KeyValue
                    keyName="Coordinates"
                    value={`${values.lat}, ${values.lng}`}
                  />
                  <KeyValue
                    keyName="Short Description"
                    value={values.caption}
                  />
                  <KeyValue
                    keyName="External Link"
                    link={values.link}
                    value={values.link}
                    external
                  />
                  <KeyValue
                    keyName="Google Place ID"
                    value={values.googlePlaceId}
                  />
                  <KeyValue
                    keyName="Has Sub-division"
                    value={values.hasChild === 1 ? "Yes" : "No"}
                  />
                  <KeyValue
                    keyName="Administrative Type"
                    value={getType(admDetails.type)}
                  />
                  <KeyValue
                    keyName="Location Count"
                    link={`/adm-areas/${admDetails.admId}/locations`}
                    value={`${admDetails.locationCount}`}
                  />
                  <KeyValue
                    keyName="Created On"
                    value={fromUnixtime(admDetails.createdOn)}
                  />
                  <KeyValue
                    keyName="Meta Title"
                    value={values?.meta?.title}
                  />
                  <KeyValue
                    keyName="Meta Description"
                    value={values?.meta?.description}
                  />
                  <KeyValue
                    keyName="Description"
                    value={
                      !!values.description && (
                        <div
                          style={{
                            backgroundColor: "white",
                            color: "black",
                            padding: "20px",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: Entities.decodeHTML5(
                              values.description.replace("&NewLine;", "")
                            ),
                          }}
                        ></div>
                      )
                    }
                  />
                </Paper>
                <Grid
                  container
                  direction="row"
                  justifyContent={admDetails.type !== 0 ? "space-between" : "flex-end"}
                  className={classes.grid}
                >
                  {
                    //show delete if it's not country
                    admDetails.type !== 0 && (
                      <Fragment>
                        {DELETE_ACCESS &&
                          <Fab
                            size="small"
                            aria-label="delete"
                            className={classes.fab}
                            onClick={handleOpenDeleteModal}
                          >
                            <DeleteIcon />
                          </Fab>}
                      </Fragment>
                    )
                  }
                  {EDIT_ACCESS &&
                    <Fab
                      size="small"
                      color="primary"
                      aria-label="edit"
                      className={classes.fab}
                      onClick={handleOpenEditModal}
                    >
                      <EditIcon />
                    </Fab>}
                </Grid>
              </Fragment>
            ) : null}
          </Box>
        </AccordionDetails>
      </Accordion>
      {EDIT_ACCESS &&
        <AddEditAdmForm
          action="edit"
          openModal={openEditModal}
          handleCloseModal={handleCloseEditModal}
          initialValues={values}
          admId={admDetails.admId}
          setValues={setValues}
          setMapdetails={setMapdetails}
          setHasChild={setHasChild}
          level={level}
        />}
      {DELETE_ACCESS &&
        <DeleteAdmForm
          openModal={openDeleteModal}
          handleCloseModal={handleCloseDeleteModal}
          admId={admDetails.admId}
          admName={!!values ? values.name.trim() : ""}
        />}
    </Fragment>
  );
}
